import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

marked.setOptions({ breaks: true, gfm: true })

export default class extends Controller {
  static targets = ["panel", "messages", "input", "sendBtn", "topBtn", "floatingStack", "suggestions", "visitorForm", "visitorName", "visitorEmail", "visitorPhone"]
  static values = {
    conversationId: Number,
    msgErrorGeneric: { type: String, default: "오류가 발생했습니다. 잠시 후 다시 시도해주세요." },
    msgErrorNoResponse: { type: String, default: "응답을 받지 못했습니다." },
    msgErrorConnection: { type: String, default: "연결 오류가 발생했습니다. 다시 시도해주세요." }
  }

  connect() {
    this._initialized = false
    this._handleScroll = () => this._updateTopButton()
    window.addEventListener("scroll", this._handleScroll, { passive: true })
    this._updateTopButton()
  }

  disconnect() {
    window.removeEventListener("scroll", this._handleScroll)
  }

  toggle() {
    const panel = this.panelTarget
    const isOpen = panel.classList.contains("opened")
    panel.classList.toggle("opened", !isOpen)

    const toggleBtn = document.getElementById("sa-toggle-btn")
    if (toggleBtn) toggleBtn.style.display = isOpen ? "" : "none"

    if (!isOpen && !this._initialized) {
      this._initialized = true
      this._fetchConversation()
    }
  }

  async _fetchConversation() {
    try {
      const res = await fetch("/chatbot/conversation", {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf, "Content-Type": "application/json" }
      })
      const data = await res.json()
      this.conversationIdValue = data.conversation_id || 0
      if (data.visitor_filled && this.hasVisitorFormTarget) {
        this.visitorFormTarget.classList.add("is-saved")
      }
    } catch (_) {}
  }

  async submitVisitor() {
    const name  = (this.hasVisitorNameTarget  ? this.visitorNameTarget.value  : "").trim()
    const email = (this.hasVisitorEmailTarget ? this.visitorEmailTarget.value : "").trim()
    const phone = (this.hasVisitorPhoneTarget ? this.visitorPhoneTarget.value : "").trim()
    if (!name && !email && !phone) return

    try {
      await fetch("/chatbot/visitor", {
        method: "PATCH",
        headers: { "X-CSRF-Token": this._csrf, "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, conversation_id: this.conversationIdValue || null })
      })
      if (this.hasVisitorFormTarget) this.visitorFormTarget.classList.add("is-saved")
    } catch (_) {}
  }

  sendSuggestion(event) {
    const prompt = event.currentTarget.dataset.prompt
    if (this.hasSuggestionsTarget) this.suggestionsTarget.remove()
    this.inputTarget.value = prompt
    this.send()
  }

  handleKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); this.send() }
  }

  autoResize() {
    const el = this.inputTarget
    el.style.height = "38px"
    el.style.height = Math.min(el.scrollHeight, 100) + "px"
    el.style.overflowY = el.scrollHeight > 100 ? "auto" : "hidden"
  }

  async send() {
    const msg = this.inputTarget.value.trim()
    if (!msg || this.sendBtnTarget.disabled) return

    if (this.hasSuggestionsTarget) this.suggestionsTarget.remove()

    this.inputTarget.value = ""
    this.inputTarget.style.height = "38px"
    this._appendMessage("user", msg)
    this.sendBtnTarget.disabled = true

    const typingEl = this._appendTyping()

    try {
      const res = await fetch("/chatbot/message", {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf, "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, conversation_id: this.conversationIdValue || null })
      })

      if (!res.ok) {
        typingEl.remove()
        this._appendMessage("assistant", this.msgErrorGenericValue)
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""
      let fullContent = ""
      let assistantBubble = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop()

        for (const line of lines) {
          if (!line.startsWith("data:")) continue
          try {
            const parsed = JSON.parse(line.slice(5).trim())
            if (parsed.type === "content") {
              if (!assistantBubble) { typingEl.remove(); assistantBubble = this._appendMessage("assistant", "") }
              fullContent += parsed.content
              assistantBubble.innerHTML = marked.parse(fullContent)
              assistantBubble.querySelectorAll("a").forEach(a => { a.target = "_blank"; a.rel = "noopener" })
              this._scrollToBottom()
            } else if (parsed.type === "error") {
              if (!assistantBubble) { typingEl.remove(); assistantBubble = this._appendMessage("assistant", "") }
              fullContent = parsed.content || this.msgErrorGenericValue
              assistantBubble.textContent = fullContent
            }
          } catch (_) {}
        }
      }

      if (!assistantBubble) { typingEl.remove(); this._appendMessage("assistant", this.msgErrorNoResponseValue) }
    } catch (_) {
      typingEl.remove()
      this._appendMessage("assistant", this.msgErrorConnectionValue)
    } finally {
      this.sendBtnTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  _appendMessage(role, content) {
    if (role === "assistant") {
      const row = document.createElement("div")
      row.className = "sa-msg-row"

      const icon = document.createElement("img")
      icon.src = "/images/klavy-cs-icon.svg"
      icon.alt = "Klavy"
      icon.className = "sa-bot-icon"
      icon.style.setProperty("border-radius", "50%", "important")
      icon.style.overflow = "hidden"

      const bubble = document.createElement("div")
      bubble.className = "sa-msg-bot"
      if (content) {
        bubble.innerHTML = marked.parse(content)
        bubble.querySelectorAll("a").forEach(a => { a.target = "_blank"; a.rel = "noopener" })
      }

      row.appendChild(icon)
      row.appendChild(bubble)
      this.messagesTarget.appendChild(row)
      this._scrollToBottom()
      return bubble
    } else {
      const div = document.createElement("div")
      div.className = "sa-msg-user"
      div.textContent = content
      this.messagesTarget.appendChild(div)
      this._scrollToBottom()
      return div
    }
  }

  _appendTyping() {
    const row = document.createElement("div")
    row.className = "sa-msg-row"

    const icon = document.createElement("img")
    icon.src = "/images/klavy-cs-icon.svg"
    icon.alt = "Klavy"
    icon.className = "sa-bot-icon"
    icon.style.setProperty("border-radius", "50%", "important")
    icon.style.overflow = "hidden"

    const dots = document.createElement("div")
    dots.className = "sa-typing"
    dots.innerHTML = "<span></span><span></span><span></span>"

    row.appendChild(icon)
    row.appendChild(dots)
    this.messagesTarget.appendChild(row)
    this._scrollToBottom()
    return row
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  _updateTopButton() {
    if (!this.hasTopBtnTarget) return
    const visible = window.scrollY > 320
    this.topBtnTarget.classList.toggle("is-visible", visible)
  }

  _scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  get _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
